// client/src/components/ErrorBoundary.tsx
import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { onNavigation, shouldResetError } from "@/lib/navigationEvents";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  eventId: string | null;
  /** the path the error happened on — navigating away clears the error (one broken page must not take the app down) */
  errorPath: string | null;
}

const CHUNK_RELOAD_KEY = "__chunk_reload_attempted__";

function isChunkLoadError(error: Error): boolean {
  const msg = error?.message || "";
  const name = error?.name || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /dynamically imported module/i.test(msg)
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, eventId: null, errorPath: null };
  }

  private unsubscribeNav: (() => void) | null = null;

  componentDidMount() {
    this.unsubscribeNav = onNavigation(this.onNavigate);
  }

  componentWillUnmount() {
    this.unsubscribeNav?.();
  }

  onNavigate = () => {
    if (shouldResetError(this.state.hasError, this.state.errorPath, window.location.pathname)) {
      this.setState({ hasError: false, eventId: null, errorPath: null });
    }
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // If this is a chunk load error and we haven't already tried reloading
    // in this session, auto-reload once to pick up the current chunk graph.
    // A loop guard via sessionStorage prevents infinite reload loops.
    if (isChunkLoadError(error)) {
      try {
        const alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        if (!alreadyTried) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
          window.location.reload();
          return { hasError: false };
        }
      } catch {
        // sessionStorage unavailable — fall through to error UI
      }
    }
    return { hasError: true, errorPath: typeof window !== "undefined" ? window.location.pathname : null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Skip Sentry noise for auto-reloaded chunk errors
    if (isChunkLoadError(error)) {
      console.warn("ChunkLoadError caught — reloading to pick up latest assets", error);
      return;
    }

    // Clear the reload guard on real errors so a future chunk error can retry
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // ignore
    }

    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
    this.setState({ eventId });

    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReport = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-background">
          <AlertCircle className="h-16 w-16 text-destructive mb-4" />
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6 max-w-md">
            We apologize for the inconvenience. Our team has been notified and is working on a fix.
          </p>
          <div className="flex gap-4">
            <Button onClick={this.handleReload} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload Page
            </Button>
            {this.state.eventId && (
              <Button onClick={this.handleReport} variant="outline">
                Report Issue
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
